// src/lib/oneOnOneShape.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  agreementFor: the forward half of a one on one, for Today.
//   2026-09-23  New. Split from oneOnOne.js so it can be tested: that file
//               imports the Supabase client, which reads import.meta.env and
//               cannot be loaded by node.
//
// THE SHAPE OF JAZZ'S ONE ON ONE, AND WHAT COUNTS AS DONE.
//
// Everything here is a pure function of its arguments. Keep it that way.
//
// The date helpers moved to localDate.js on 2026-09-23. Re-exported so every
// existing call site is unchanged.
//
// EXPLICIT .js ON THE IMPORT. Vite resolves either form, node only resolves
// this one, and the tests load this file directly under node.

import { dayKey, addDays, startOfDay, parseDay } from './localDate.js'

export { dayKey, addDays }

// The four review areas, in his order, from his own guide. The prompts under
// them are his wording rather than ours, because a field labelled "Pipeline"
// with no prompt gets "fine" typed into it.
export const REVIEW = [
  {
    key: 'pipeline',
    label: 'Pipeline and aged opportunities',
    prompt: 'What is sitting there, and what is going cold.',
  },
  {
    key: 'unsold',
    label: "Yesterday's unsold",
    prompt: 'His rule is before the opportunity goes cold, which means today rather than this week.',
  },
  {
    key: 'activity',
    label: 'Activity against goals',
    prompt: 'Calls, two way contacts, appointments, shows, deliveries and referrals.',
  },
  {
    key: 'obstacles',
    label: 'Obstacles and priorities',
    prompt: 'What is actually in their way today.',
  },
]

// THE COACHING MOVE. Five parts, and they are five fields rather than one notes
// box on purpose.
//
// His guide: "Use coaching language: describe the observed behavior, explain
// the impact, demonstrate the correction, require a repetition and set a time
// to inspect it again." His very next point rules out the alternative: "Avoid
// vague feedback such as be more aggressive or try harder."
//
// One box lets a manager write "talked about follow up" and feel finished.
// Five fields do not.
export const MOVE = [
  {
    key: 'behavior',
    label: 'What you actually saw',
    prompt: 'The behaviour, not the attitude. Something you watched happen.',
  },
  {
    key: 'impact',
    label: 'What it cost',
    prompt: 'Why it matters, in terms of the deal or the customer.',
  },
  {
    key: 'correction',
    label: 'What you showed them instead',
    prompt: 'Demonstrate it. Not a description of a better approach, the words or the move.',
  },
  {
    key: 'repetition',
    label: 'What they did back',
    prompt: 'He requires a repetition. They say it or do it, in front of you, before this ends.',
  },
]

// Vague feedback, which his guide names and rules out. Matching on phrases is
// blunt and it is meant to be: this warns, it never blocks. A manager who has a
// real reason to write "be more aggressive" can still save it, and a manager
// who typed it without thinking gets one nudge.
const VAGUE = [
  'be more aggressive',
  'try harder',
  'more aggressive',
  'step it up',
  'work harder',
  'be better',
  'more effort',
  'needs to want it',
  'hungrier',
]

export function vagueIn(text) {
  const said = String(text || '').toLowerCase()
  if (!said.trim()) return null
  return VAGUE.find((p) => said.includes(p)) || null
}

const filled = (v) => String(v || '').trim().length > 0

// A one on one is RECORDED as soon as anything is in it, because a manager who
// only got through the pipeline before the phone rang still had the meeting.
export function hasAnything(row) {
  return [...REVIEW, ...MOVE].some((f) => filled(row?.[f.key]))
}

// But the COACHING MOVE is all or nothing. Four fifths of it is a description
// of a problem with no correction attached, which is the thing his guide exists
// to prevent. The date is part of the move, not an extra.
export function moveComplete(row) {
  return MOVE.every((f) => filled(row?.[f.key])) && filled(row?.inspect_on)
}

export function moveProgress(row) {
  const parts = MOVE.filter((f) => filled(row?.[f.key])).length + (filled(row?.inspect_on) ? 1 : 0)
  return { done: parts, total: MOVE.length + 1 }
}

// Due to be looked at again. Overdue is a separate word from due because a
// manager with three overdue inspections has a different problem from one with
// three due today.
export function inspectionState(row, today = new Date()) {
  if (!row?.inspect_on) return 'none'
  if (row.inspected_at) return 'done'
  const t = startOfDay(today)
  const due = parseDay(row.inspect_on)
  if (due < t) return 'overdue'
  if (due.getTime() === t.getTime()) return 'today'
  return 'ahead'
}


// WHAT A SALESPERSON SEES OF THEIR OWN ONE ON ONE, ON TODAY.
//
// 0039 lets them read the whole record because they were in the room. Today
// shows only the forward half: what their manager showed them, and when their
// manager is coming back to look. The critique (behaviour and impact) stays on
// the record and off the front door, because a morning screen that opens on
// what you did wrong is a screen people stop opening.
//
// Null when there is nothing to carry forward: no correction written, the
// manager already came back and looked, or the meeting is more than three
// weeks old with no return date. `due` tells the card how to phrase the date.
export const AGREEMENT_STALE_DAYS = 21

export function agreementFor(row, today = dayKey()) {
  if (!row) return null
  const correction = String(row.correction || '').trim()
  if (!correction) return null
  if (row.inspected_at) return null
  if (!row.inspect_on && row.held_on < addDays(today, -AGREEMENT_STALE_DAYS)) return null
  let due = null
  if (row.inspect_on) due = row.inspect_on < today ? 'overdue' : row.inspect_on === today ? 'today' : 'upcoming'
  return { correction, heldOn: row.held_on, inspectOn: row.inspect_on || null, managerId: row.manager_id, due }
}
